import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Grid,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import WebhookOutlinedIcon from '@mui/icons-material/WebhookOutlined';
import { useCompanyCore } from '../../../../core/modules/company';
import { inventoryCountService, WebhookConfig } from '../../../../core/modules/inventoryCount/services/api';

const InventoryCountTab = () => {
  const { company } = useCompanyCore();
  const companyId = company?.id ? Number(company.id) : null;

  const [config, setConfig] = useState<WebhookConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [webhookUrlInput, setWebhookUrlInput] = useState('');
  const [savingWebhook, setSavingWebhook] = useState(false);

  const [savingEnabled, setSavingEnabled] = useState(false);

  const loadConfig = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await inventoryCountService.getWebhookConfig(companyId);
      setConfig(data);
      setWebhookUrlInput(data.webhookUrl ?? '');
    } catch (err) {
      setError('No se pudo cargar la configuración de Toma de Inventario.');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSaveWebhook = async () => {
    if (!companyId) return;
    setSavingWebhook(true);
    setError(null);
    setInfo(null);
    try {
      const trimmed = webhookUrlInput.trim();
      const result = await inventoryCountService.updateWebhookConfig(companyId, trimmed || null);
      setConfig(result);
      setWebhookUrlInput(result.webhookUrl ?? '');
      setInfo(result.webhookUrl ? 'URL de webhook guardada.' : 'Se quitó la URL de webhook: el envío quedó desactivado.');
    } catch (err: any) {
      setError(
        err?.response?.data?.detail?.[0]?.msg ||
          err?.response?.data?.detail ||
          'No se pudo guardar la URL de webhook. Verifica que sea una URL válida (http:// o https://).'
      );
    } finally {
      setSavingWebhook(false);
    }
  };

  // Interruptor general del modulo (Company.inventory_count_enabled). Esta
  // pestaña solo la ve un superadmin (Company.tsx redirige a cualquier otro
  // rol), asi que no hace falta un chequeo de permisos adicional en el
  // front -- el backend igual lo vuelve a exigir por su cuenta.
  const handleToggleEnabled = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!companyId) return;
    const nextValue = event.target.checked;
    setSavingEnabled(true);
    setError(null);
    setInfo(null);
    try {
      const result = await inventoryCountService.updateWebhookConfig(companyId, config?.webhookUrl ?? null, nextValue);
      setConfig(result);
      setInfo(nextValue ? 'Módulo habilitado para esta empresa.' : 'Módulo deshabilitado para esta empresa.');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'No se pudo cambiar el estado del módulo.');
    } finally {
      setSavingEnabled(false);
    }
  };

  if (loading) {
    return (
      <Box className="flex items-center justify-center p-8">
        <Card className="w-full max-w-2xl">
          <CardContent>
            <Skeleton variant="rectangular" height={280} />
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box className="flex items-center justify-center p-4 sm:p-8">
      <Card className="w-full max-w-2xl shadow-xl">
        <CardContent className="p-6 sm:p-8">
          <Stack spacing={3}>
            <Box display="flex" alignItems="center" gap={1}>
              <WebhookOutlinedIcon color="action" fontSize="small" />
              <Typography variant="h5" component="h2" className="font-bold">
                Toma de Inventario
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {info && (
              <Alert severity="success" onClose={() => setInfo(null)}>
                {info}
              </Alert>
            )}

            <Box display="flex" alignItems="center" gap={1}>
              <FormControlLabel
                control={
                  <Switch checked={!!config?.inventoryCountEnabled} onChange={handleToggleEnabled} disabled={savingEnabled} />
                }
                label={
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={config?.inventoryCountEnabled ? 'success.main' : 'text.secondary'}
                  >
                    {config?.inventoryCountEnabled ? 'Módulo habilitado para esta empresa' : 'Módulo deshabilitado para esta empresa'}
                  </Typography>
                }
              />
              {savingEnabled && <CircularProgress size={16} />}
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Webhook de conciliación
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                URL a la que se envía la conciliación final cuando se usa el botón "Enviar por webhook"
                en cualquier sesión de esta empresa (ej. el ERP de un cliente 3PL). Dejar vacío desactiva el envío.
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={8}>
                  <TextField
                    label="URL del webhook"
                    placeholder="https://tu-sistema.com/webhooks/inventario"
                    fullWidth
                    value={webhookUrlInput}
                    onChange={(e) => setWebhookUrlInput(e.target.value)}
                    helperText={
                      config?.webhookUrl
                        ? `Configurada: ${config.webhookUrl}`
                        : 'Todavía no hay una URL configurada para esta empresa.'
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="contained"
                    fullWidth
                    disabled={savingWebhook || webhookUrlInput.trim() === (config?.webhookUrl ?? '')}
                    onClick={handleSaveWebhook}
                  >
                    {savingWebhook ? <CircularProgress size={20} /> : 'Guardar'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default InventoryCountTab;
